/**
 * Registre des validators custom pour les conditions de graphiques
 *
 * Ce registre permet d'enregistrer et de récupérer des validators custom
 * utilisés pour évaluer des conditions complexes sur les bâtiments et les sources.
 */

import type { ConditionBlock, Building, Source, BuildingSource } from "@types"

/**
 * Type d'une fonction validator custom
 * @param value - La valeur attendue de la condition (peut être undefined si field/value optionnels)
 * @param context - Le contexte enrichi (any, peut contenir building, sources, energyuseGraph, etc.)
 * @returns true si la condition est respectée, false sinon
 */
export type CustomValidator = (value: any, context: any) => boolean

/**
 * Type d'une fonction pour construire le contexte enrichi
 * @param data - Les données (building ou sources) pour construire le contexte
 * @param condition - La condition à évaluer
 * @returns Le contexte enrichi
 */
export type ContextBuilder = (
    data: Building | (Source | BuildingSource)[],
    condition: ConditionBlock
) => any

/**
 * Structure d'un validator avec son buildContext optionnel
 */
export type ValidatorWithContext = {
    validator: CustomValidator
    buildContext?: ContextBuilder
}

/**
 * Registre des validators custom avec leur buildContext optionnel
 */
const validatorsRegistry = new Map<string, ValidatorWithContext>()

/**
 * Enregistre un validator custom dans le registre
 * @param id - Identifiant unique du validator
 * @param validatorOrObject - Fonction de validation ou objet avec validator et buildContext optionnel
 * @param buildContext - Fonction optionnelle pour construire le contexte (si validatorOrObject est une fonction)
 */
export function registerValidator(
    id: string,
    validatorOrObject: CustomValidator | ValidatorWithContext,
    buildContext?: ContextBuilder
): void {
    if (validatorsRegistry.has(id)) {
        console.warn(
            `Le validator '${id}' est déjà enregistré. Il sera remplacé.`
        )
    }

    let validatorWithContext: ValidatorWithContext

    if (typeof validatorOrObject === "function") {
        // Format simple : juste la fonction validator
        validatorWithContext = {
            validator: validatorOrObject,
            buildContext,
        }
    } else {
        // Format objet : { validator, buildContext? }
        validatorWithContext = validatorOrObject
    }

    validatorsRegistry.set(id, validatorWithContext)
}

/**
 * Récupère un validator custom depuis le registre
 * @param id - Identifiant du validator
 * @returns Le validator avec son buildContext ou undefined s'il n'existe pas
 */
export function getValidator(id: string): ValidatorWithContext | undefined {
    return validatorsRegistry.get(id)
}

/**
 * Vérifie si un validator existe dans le registre
 * @param id - Identifiant du validator
 * @returns true si le validator existe
 */
export function hasValidator(id: string): boolean {
    return validatorsRegistry.has(id)
}
